export interface CampaignExperimentalDto {
  id: string;
  name: string;
  description?: string;
  campaignType: CampaignExperimentalType;
  status: CampaignExperimentalStatus;
  assignment: CampaignAssignmentDto;
  couponTemplateId?: string;
  automationSettings?: AutomationSettingsDto;
  scheduleSettings?: ScheduleSettingsDto;
  notificationPlan: NotificationChannel[];
  statistics?: CampaignStatisticsDto;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  triggerType?: string;
  audienceType?: string;
  notificationChannel?: string;
  validity?: string;
}

export enum CampaignExperimentalType {
  ONE_TIME = 'ONE_TIME',
  RECURRING = 'RECURRING'
}

export enum CampaignExperimentalStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  SCHEDULED = 'SCHEDULED',
  PAUSED = 'PAUSED',
  ENDED = 'ENDED',
  CANCELLED = 'CANCELLED',
  COMPLETED = 'COMPLETED',
}

export enum NotificationChannel {
  EMAIL = 'EMAIL',
  PUSH_NOTIFICATION = 'PUSH_NOTIFICATION'
}

export enum AssignmentType {
  ALL_MEMBERS = 'ALL_MEMBERS',
  SEGMENTS = 'SEGMENTS',
  CIRCLES = 'CIRCLES'
}

export interface CampaignAssignmentDto {
  type: AssignmentType;
  segmentIds?: string[];
  circleIds?: string[];
  estimatedReach?: number;
}

export interface AutomationSettingsDto {
  triggerEvent: AutomationTriggerEvent;
  timingOffset: number;
  timingOffsetUnit: TimingOffsetUnit;
  triggerTime?: string; // HH:mm format for specific time triggers
  eventConditions?: EventConditionDto[];
}

export enum AutomationTriggerEvent {
  BIRTHDAY = 'BIRTHDAY',
  ANNIVERSARY = 'ANNIVERSARY',
  FIRST_PURCHASE = 'FIRST_PURCHASE',
  PURCHASE_MILESTONE = 'PURCHASE_MILESTONE',
  POINTS_MILESTONE = 'POINTS_MILESTONE',
  TIER_UPGRADE = 'TIER_UPGRADE',
  INACTIVITY_PERIOD = 'INACTIVITY_PERIOD'
}

export enum TimingOffsetUnit {
  MINUTES = 'MINUTES',
  HOURS = 'HOURS',
  DAYS = 'DAYS',
  WEEKS = 'WEEKS'
}

export interface EventConditionDto {
  field: string;
  operator: ConditionOperator;
  value: string | number;
}

export enum ConditionOperator {
  EQUALS = 'EQUALS',
  GREATER_THAN = 'GREATER_THAN',
  LESS_THAN = 'LESS_THAN',
  GREATER_THAN_OR_EQUAL = 'GREATER_THAN_OR_EQUAL',
  LESS_THAN_OR_EQUAL = 'LESS_THAN_OR_EQUAL',
  CONTAINS = 'CONTAINS',
  IN = 'IN'
}

export interface ScheduleSettingsDto {
  startDate: string; // ISO 8601 date-time
  endDate?: string; // ISO 8601 date-time
  timezone: string;
  executionTime?: string; // HH:mm format for specific execution time
}

export interface CampaignStatisticsDto {
  totalReach: number;
  totalSent: number;
  totalDelivered: number;
  totalOpened: number;
  totalClicked: number;
  totalRedemptions: number;
  conversionRate: number;
  revenue: number;
  currency: string;
  lastUpdated: string;
}

// Request DTOs for API operations
export interface CreateCampaignExperimentalRequestDto {
  name: string;
  description?: string;
  campaignType: CampaignExperimentalType;
  assignment: CampaignAssignmentDto;
  couponTemplateId?: string;
  automationSettings?: AutomationSettingsDto;
  scheduleSettings?: ScheduleSettingsDto;
  notificationPlan: NotificationChannel[];
}

export interface UpdateCampaignExperimentalRequestDto extends CreateCampaignExperimentalRequestDto {
  id: string;
}

export interface CampaignStatusUpdateRequestDto {
  status: CampaignExperimentalStatus;
  reason?: string;
}

// Response DTOs
export interface CampaignExperimentalListResponseDto {
  campaigns: CampaignExperimentalDto[];
  totalCount: number;
  pageSize: number;
  pageNumber: number;
  totalPages: number;
}

export interface CampaignValidationResponseDto {
  isValid: boolean;
  errors: ValidationErrorDto[];
  warnings: ValidationWarningDto[];
}

export interface ValidationErrorDto {
  field: string;
  code: string;
  message: string;
}

export interface ValidationWarningDto {
  field: string;
  code: string;
  message: string;
}

// Filter and search DTOs
export interface CampaignExperimentalFilterDto {
  name?: string;
  status?: CampaignExperimentalStatus[];
  campaignType?: CampaignExperimentalType[];
  startDateFrom?: string;
  startDateTo?: string;
  endDateFrom?: string;
  endDateTo?: string;
}

export interface CampaignExperimentalSearchRequestDto {
  filters?: CampaignExperimentalFilterDto;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  pageNumber?: number;
  pageSize?: number;
}

// Analytics DTOs
export interface CampaignAnalyticsRequestDto {
  campaignIds: string[];
  dateFrom?: string;
  dateTo?: string;
  metrics: AnalyticsMetric[];
  groupBy?: AnalyticsGroupBy;
}

export enum AnalyticsMetric {
  REACH = 'REACH',
  SENT = 'SENT',
  DELIVERED = 'DELIVERED',
  OPENED = 'OPENED',
  CLICKED = 'CLICKED',
  REDEMPTIONS = 'REDEMPTIONS',
  CONVERSION_RATE = 'CONVERSION_RATE',
  REVENUE = 'REVENUE'
}

export enum AnalyticsGroupBy {
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  CAMPAIGN = 'CAMPAIGN',
  CHANNEL = 'CHANNEL'
}

export interface CampaignAnalyticsResponseDto {
  data: AnalyticsDataPointDto[];
  summary: CampaignStatisticsDto;
}

export interface AnalyticsDataPointDto {
  date: string;
  campaignId?: string;
  campaignName?: string;
  channel?: NotificationChannel;
  metrics: Record<AnalyticsMetric, number>;
}

// Bulk operations DTOs
export interface BulkCampaignOperationRequestDto {
  campaignIds: string[];
  operation: BulkOperation;
  parameters?: Record<string, any>;
}

export enum BulkOperation {
  ACTIVATE = 'ACTIVATE',
  PAUSE = 'PAUSE',
  CANCEL = 'CANCEL',
  DELETE = 'DELETE',
  DUPLICATE = 'DUPLICATE'
}

export interface BulkOperationResponseDto {
  totalRequested: number;
  successCount: number;
  failureCount: number;
  results: BulkOperationResultDto[];
}

export interface BulkOperationResultDto {
  campaignId: string;
  success: boolean;
  error?: string;
}
